import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography
} from '@material-ui/core'
import {
  CheckCircle as CheckCircleIcon,
  PauseCircleOutline as PauseCircleIcon,
  Replay as ReplayIcon
} from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { theme } from '../../../../themes/gashogar/theme'
import { InvoiceCyclePollingState } from '../../hooks/useInvoiceCyclePolling'

interface InvoiceCycleProcessingProgressProps {
  isProcessing: boolean
  pollingState: InvoiceCyclePollingState
  onResume: () => void
  onBack: () => void
  isCompleted?: boolean
  onNavigate?: () => void
  completedMessage?: string
  processedElements?: number
  totalProcessingElements?: number
  /** 'page' renders a spacious full-screen layout; 'floating' renders a compact row layout for the bottom-right notification */
  variant?: 'page' | 'floating'
}

export const InvoiceCycleProcessingProgress: React.FC<InvoiceCycleProcessingProgressProps> = ({
  isProcessing,
  pollingState,
  onResume,
  onBack,
  isCompleted = false,
  onNavigate,
  completedMessage,
  processedElements = 0,
  totalProcessingElements = 0,
  variant = 'page'
}) => {
  const [t] = useTranslation(['invoiceCyclesProcessingPage'])

  const hasBatchProgress = totalProcessingElements > 0
  const progressPercentage = hasBatchProgress
    ? Math.round((processedElements / totalProcessingElements) * 100)
    : (pollingState.currentRetry / pollingState.maxRetries) * 100

  // ─── Compact floating variant ────────────────────────────────────────────────
  if (variant === 'floating') {
    // Initializing: polling not yet started but still processing (avoids flash of "stable")
    if (isProcessing && !pollingState.isActive && !pollingState.isPaused) {
      return (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <CircularProgress size={14} thickness={5} color="primary" />
          <Typography variant="caption" color="textSecondary">
            {t('invoiceCyclesProcessingPage:processing.checking')}
          </Typography>
        </Box>
      )
    }

    // Active polling
    if (isProcessing && pollingState.isActive) {
      return (
        <Box display="flex" flexDirection="column" style={{ gap: 6 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={14} thickness={5} color="inherit" />
            <Typography variant="caption">
              {t('invoiceCyclesProcessingPage:processing.checking')}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            style={{ borderRadius: 2, height: 4, color: 'inherit' }}
          />
          <Typography variant="caption" color="textSecondary">
            {hasBatchProgress
              ? t('invoiceCyclesProcessingPage:processing.batchProgress', {
                  percentage: progressPercentage
                })
              : t('invoiceCyclesProcessingPage:processing.attempt', {
                  current: pollingState.currentRetry,
                  max: pollingState.maxRetries
                })}
          </Typography>
        </Box>
      )
    }

    // Paused – max retries reached
    if (pollingState.isPaused) {
      return (
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <PauseCircleIcon style={{ fontSize: 18, color: theme.palette.text.secondary }} />
            <Typography variant="caption">
              {t('invoiceCyclesProcessingPage:processing.maxAttemptsReached')}
            </Typography>
          </Box>
          <Tooltip title={t('invoiceCyclesProcessingPage:processing.actions.resume') as string}>
            <IconButton size="small" color="inherit" onClick={onResume}>
              <ReplayIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }

    // Completed
    if (isCompleted && onNavigate) {
      return (
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8 }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CheckCircleIcon style={{ fontSize: 18, color: theme.palette.success.main }} />
            <Typography variant="caption">
              {completedMessage || t('invoiceCyclesProcessingPage:processing.completed.title')}
            </Typography>
          </Box>
          <Button
            size="small"
            color="default"
            variant="text"
            onClick={onNavigate}
            style={{ padding: '2px 8px', fontSize: '0.7rem', flexShrink: 0 }}
          >
            {t('invoiceCyclesProcessingPage:processing.actions.continue')}
          </Button>
        </Box>
      )
    }

    // Stable / default (should rarely be visible)
    return (
      <Typography variant="caption" color="textSecondary">
        {t('invoiceCyclesProcessingPage:processing.stable')}
      </Typography>
    )
  }

  // ─── Full-page variant ────────────────────────────────────────────────────────
  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      mt={theme.spacing(1)}
      mb={theme.spacing(2)}
    >
      {/* Active processing */}
      {isProcessing && pollingState.isActive && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          mt={theme.spacing(1)}
          width="100%"
          maxWidth="400px"
        >
          {/* Spinner: indeterminado hasta que llegue batch progress, luego determinado con % */}
          <Box position="relative" display="inline-flex" mb={theme.spacing(2)}>
            <CircularProgress
              variant={hasBatchProgress ? 'determinate' : 'indeterminate'}
              value={hasBatchProgress ? progressPercentage : undefined}
              size={120}
              thickness={2.5}
              color="primary"
            />
            {hasBatchProgress && (
              <Box
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                right={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="body2">{`${progressPercentage}%`}</Typography>
              </Box>
            )}
          </Box>

          {!hasBatchProgress && (
            <Typography variant="body2" color="textSecondary">
              {t('invoiceCyclesProcessingPage:processing.attempt', {
                current: pollingState.currentRetry,
                max: pollingState.maxRetries
              })}
            </Typography>
          )}
        </Box>
      )}

      {/* Paused */}
      {pollingState.isPaused && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          mt={theme.spacing(1)}
          maxWidth="500px"
        >
          <PauseCircleIcon
            style={{
              fontSize: 64,
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2)
            }}
          />
          <Typography variant="h6" gutterBottom align="center">
            {t('invoiceCyclesProcessingPage:processing.maxAttemptsReached')}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            style={{ marginBottom: theme.spacing(3) }}
          >
            {t('invoiceCyclesProcessingPage:processing.maxAttemptsDescription')}
          </Typography>
          <Box display="flex" justifyContent="center" gridGap={theme.spacing(2)}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              onClick={onResume}
            >
              {t('invoiceCyclesProcessingPage:processing.actions.resume')}
            </Button>
            <Button variant="outlined" onClick={onBack}>
              {t('invoiceCyclesProcessingPage:processing.actions.back')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Completed */}
      {isCompleted && onNavigate && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          mt={theme.spacing(1)}
          maxWidth="500px"
        >
          <CheckCircleIcon
            style={{
              fontSize: 64,
              color: theme.palette.success.main,
              marginBottom: theme.spacing(2)
            }}
          />
          <Typography variant="h6" gutterBottom align="center">
            {t('invoiceCyclesProcessingPage:processing.completed.title')}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            style={{ marginBottom: theme.spacing(3) }}
          >
            {completedMessage || t('invoiceCyclesProcessingPage:processing.completed.description')}
          </Typography>
          <Box display="flex" justifyContent="center" gridGap={theme.spacing(2)}>
            <Button variant="contained" color="primary" onClick={onNavigate}>
              {t('invoiceCyclesProcessingPage:processing.actions.continue')}
            </Button>
            <Button variant="outlined" onClick={onBack}>
              {t('invoiceCyclesProcessingPage:processing.actions.back')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Stable */}
      {!isProcessing && !pollingState.isPaused && !isCompleted && (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(2) }}>
          {t('invoiceCyclesProcessingPage:processing.stable')}
        </Typography>
      )}
    </Box>
  )
}
